/**
 * UNRAVEL — pop-in overlay machinery.
 *
 * The Priority List section was written against the previous theme's <newsletter-popup>
 * element, which supplied the modal behaviour. Without a definition the element is
 * unknown markup, so the popup laid itself out inline at the foot of every page.
 *
 * Contract kept identical to the section's attributes:
 *   apparition-delay="<seconds>"  only-once  open
 */
const SEEN_KEY = 'unravel:priority-popup-seen';
const FILLED_KEY = 'theme:popup-filled';

const stored = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

class NewsletterPopup extends HTMLElement {
  #shown = false;
  #timer = null;

  connectedCallback() {
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    this.hidden = !this.hasAttribute('open');

    // An ancestor with a transform/filter would become the containing block for
    // the panel's fixed positioning; re-parenting to <body> keeps it measured
    // against the viewport.
    if (this.parentElement !== document.body) document.body.appendChild(this);

    // A submission renders the section back with `open`, so the success state is
    // shown even though the visitor is now marked as having seen it.
    if (this.hasAttribute('open')) return this.#show({ remember: false });
    if (stored(FILLED_KEY) === 'true') return;
    if (this.hasAttribute('only-once') && stored(SEEN_KEY) === 'true') return;

    const delay = Number(this.getAttribute('apparition-delay') || 0) * 1000;
    this.#timer = setTimeout(() => this.#show({ remember: true }), delay);

    this.#onKeydown = (event) => {
      if (event.key === 'Escape' && !this.hidden) this.close();
    };
    document.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    document.removeEventListener('keydown', this.#onKeydown);
  }

  #onKeydown = null;

  #backdrop = null;

  #show({ remember }) {
    if (this.#shown) return;
    this.#shown = true;
    this.hidden = false;
    this.setAttribute('open', '');

    this.#backdrop = document.createElement('div');
    this.#backdrop.className = 'uv-popin-backdrop';
    this.#backdrop.addEventListener('click', () => this.close());
    document.body.insertBefore(this.#backdrop, this);

    requestAnimationFrame(() => {
      this.classList.add('pop-in--visible');
      this.#backdrop?.classList.add('is-visible');
    });

    if (remember && this.hasAttribute('only-once')) {
      try {
        localStorage.setItem(SEEN_KEY, 'true');
      } catch {
        /* private mode — the popup simply shows again next visit */
      }
    }

    this.querySelector('input:not([type="hidden"]), button')?.focus({ preventScroll: true });
  }

  close() {
    this.classList.remove('pop-in--visible');
    this.hidden = true;
    this.removeAttribute('open');
    clearTimeout(this.#timer);
    this.#backdrop?.remove();
    this.#backdrop = null;
  }
}

class DialogCloseButton extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', () => {
      this.closest('newsletter-popup')?.close();
    });
  }
}

if (!customElements.get('newsletter-popup')) customElements.define('newsletter-popup', NewsletterPopup);
if (!customElements.get('dialog-close-button')) customElements.define('dialog-close-button', DialogCloseButton);
